import { Building2, ChevronRight, Mail } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { EmptyState } from '@/components/shared/states';
import { SearchInput } from '@/components/shared/search-input';
import { UnityLogo } from '@/components/shared/unity-logo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/components/ui/toast';
import { switchChurchAndOpen, CONSOLE_HOME } from '@/lib/switch-church-workspace';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { groupChurchMemberships, type ChurchOrgGroup } from '@/pages/churches/group-church-memberships';
import { useAuthStore } from '@/stores/auth-store';
import {
  getSavedChurchId,
  useChurchStore,
  type ChurchMembershipSummary,
} from '@/stores/church-store';
import { useChurchSwitchingStore } from '@/stores/church-switching-store';
import { cn } from '@/utils/cn';

import '../auth/auth.css';

interface PendingChurchInvite {
  id: string;
  churchId: string;
  churchName: string;
  logoUrl: string | null;
  systemRole: ChurchMembershipSummary['membership']['systemRole'];
  expiresAt: string;
  invitedAt: string;
}

/** Search appears once a church has enough campuses that scanning the full list is awkward. */
const CAMPUS_SEARCH_THRESHOLD = 6;

export const SelectChurchPage = () => {
  const { t } = useTranslation('churches');
  const { t: tAuth } = useTranslation('auth');
  const { t: tBranches } = useTranslation('branches');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useAuthStore((state) => state.signOut);
  const memberships = useChurchStore((state) => state.memberships);
  const selectChurch = useChurchStore((state) => state.selectChurch);
  const openSupportSession = useChurchStore((state) => state.openSupportSession);
  const supportChurchId = useAuthStore((state) => state.user?.supportAccess?.churchId ?? null);

  useEffect(() => {
    if (!supportChurchId) return;
    void openSupportSession(supportChurchId).then(() => {
      void navigate('/home', { replace: true });
    });
  }, [navigate, openSupportSession, supportChurchId]);
  const restore = useChurchStore((state) => state.restore);
  const switching = useChurchSwitchingStore((state) => state.active);

  const invitesQuery = useQuery({
    queryKey: ['church-invites', 'mine'],
    queryFn: () =>
      api.get<{ data: PendingChurchInvite[] }>('/church-invites/mine', { withoutChurch: true }),
  });

  const pendingInvites = invitesQuery.data?.data ?? [];
  const invitesReady = !invitesQuery.isLoading;

  const groups = useMemo(() => groupChurchMemberships(memberships), [memberships]);
  const lastChurchId = useMemo(() => getSavedChurchId(), []);
  const hasMemberships = memberships.length > 0;
  const hasInvites = invitesReady && pendingInvites.length > 0;
  const showEmptyState = invitesReady && !hasMemberships && !hasInvites;

  const acceptInvite = useMutation({
    mutationFn: (inviteId: string) =>
      api.post<{ churchId: string; membershipId: string }>(
        `/church-invites/${inviteId}/accept`,
        {},
        { withoutChurch: true },
      ),
    onSuccess: async (result, inviteId) => {
      const invite = pendingInvites.find((row) => row.id === inviteId);
      toast.success(
        t('select.invitationAccepted', { churchName: invite?.churchName ?? '' }),
      );
      await switchChurchAndOpen({
        action: async () => {
          await restore();
          await queryClient.invalidateQueries({ queryKey: ['church-invites', 'mine'] });
          await selectChurch(result.churchId);
        },
        navigate,
        mode: 'join',
        churchName: invite?.churchName,
      });
    },
    onError: (error) => {
      const message =
        error instanceof ApiError
          ? tErrors(error.code, error.params)
          : tAuth('acceptInvite.invalid');
      toast.error(message);
    },
  });

  const resolveCampusName = (churchId: string): string | undefined => {
    const row = memberships.find((item) => item.church.id === churchId);
    if (!row) return undefined;
    const group = groups.find((item) =>
      item.campuses.some((campus) => campus.church.id === churchId),
    );
    const multiCampus = (group?.campuses.length ?? 0) > 1;
    return multiCampus
      ? campusLabel(row, tBranches('switcher.headOffice'))
      : (group?.name ?? row.church.name);
  };

  const openCampus = async (churchId: string, to = CONSOLE_HOME) => {
    await switchChurchAndOpen({
      action: () => selectChurch(churchId),
      navigate,
      destination: to,
      churchName: resolveCampusName(churchId),
    });
  };

  const setupBranch = async (group: ChurchOrgGroup) => {
    const head = group.campuses.find((row) => !row.church.parentChurchId);
    if (!head) return;
    await openCampus(head.church.id, '/branches?create=1');
  };

  const leave = async () => {
    await signOut();
    void navigate('/sign-in', { replace: true });
  };

  if (supportChurchId) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <p className="text-body text-on-brand">{tAuth('supportAccess.opening')}</p>
      </div>
    );
  }

  return (
    <div className="auth-brand-shell flex min-h-dvh flex-col text-on-brand">
      <div className="auth-brand-shell-content mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 py-8 sm:py-12">
        <header className="mb-8 space-y-6 border-b border-on-brand/20 pb-8 sm:mb-10">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="shrink-0 transition-opacity hover:opacity-90">
              <UnityLogo
                variant="horizontal-on-dark"
                className="h-10"
                label={tCommon('app.name')}
              />
            </Link>
            <Button
              type="button"
              variant="link"
              className="shrink-0 text-on-brand hover:text-on-brand/90"
              onClick={() => void leave()}
            >
              {tAuth('signOut')}
            </Button>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="min-w-0 space-y-2">
              <h1 className="text-page-title font-semibold text-on-brand">
                {showEmptyState ? t('select.emptyTitle') : t('select.title')}
              </h1>
              {showEmptyState ? null : invitesReady ? (
                <p className="max-w-xl text-body text-on-brand/85">
                  {hasInvites
                    ? t('select.hasInvitationsDescription')
                    : hasMemberships
                      ? t('select.description')
                      : t('select.emptyDescription')}
                </p>
              ) : null}
            </div>
            <Button
              asChild
              variant="outline"
              className="shrink-0 self-start border-on-brand/35 bg-on-brand/10 text-on-brand hover:bg-on-brand/15 sm:self-auto"
            >
              <Link to="/churches/new">
                {showEmptyState ? t('select.createFirst') : t('select.createAnother')}
              </Link>
            </Button>
          </div>
        </header>

        {showEmptyState ? (
          <>
            <p className="mb-6 max-w-xl text-body-lg font-medium text-on-brand sm:text-section-title">
              {t('select.emptyInviteHint')}
            </p>
            <Card className="border-dashed text-fg shadow-e3">
              <EmptyState
                icon={<Building2 className="h-6 w-6" />}
                title={t('select.emptyStateTitle')}
                body={t('select.emptyStateBody')}
                action={
                  <Button asChild>
                    <Link to="/churches/new">{t('select.createFirst')}</Link>
                  </Button>
                }
              />
            </Card>
          </>
        ) : null}

        {hasInvites ? (
          <section className="mb-6 space-y-3">
            <h2 className="text-body font-semibold text-on-brand">{t('select.invitationsTitle')}</h2>
            <ul className="flex flex-col gap-4">
              {pendingInvites.map((invite) => (
                <li key={invite.id}>
                  <Card className="overflow-hidden border-brand/25 text-fg shadow-e3">
                    <CardHeader className="flex-row items-start gap-4 p-5">
                      <ChurchMark logoUrl={invite.logoUrl} name={invite.churchName} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-3">
                          <Mail className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
                          <div className="min-w-0">
                            <h3 className="truncate text-card-title font-semibold text-fg">
                              {invite.churchName}
                            </h3>
                            <p className="mt-0.5 text-body-sm text-fg-secondary">
                              {t('select.invitationDescription', {
                                role: t(`membership.role.${invite.systemRole}`),
                              })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardFooter>
                      <Button
                        loading={acceptInvite.isPending}
                        disabled={switching || acceptInvite.isPending}
                        onClick={() => acceptInvite.mutate(invite.id)}
                      >
                        {t('select.acceptInvitation')}
                      </Button>
                    </CardFooter>
                  </Card>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {hasMemberships && hasInvites ? (
          <h2 className="mb-3 text-body font-semibold text-on-brand">{t('select.yourChurchesTitle')}</h2>
        ) : null}

        <ul className="flex flex-col gap-4">
          {groups.map((group) => {
            const campusRow = group.campuses[0]!;
            const multiCampus = group.campuses.length > 1;

            const headRow = group.campuses.find((row) => !row.church.parentChurchId);
            const canSetupBranch =
              Boolean(headRow) && headRow!.membership.systemRole !== 'congregant';

            return (
              <li key={group.orgId}>
                <Card className="overflow-hidden text-fg shadow-e3">
                  {multiCampus ? (
                    <CardHeader className="flex-row items-start gap-4 p-5">
                      <ChurchMark logoUrl={group.logoUrl} name={group.name} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <h2 className="truncate text-card-title font-semibold text-fg">
                              {group.name}
                            </h2>
                            <p className="mt-0.5 text-body-sm text-fg-secondary">
                              {t('select.campusCount', { count: group.campuses.length })}
                            </p>
                          </div>
                          {canSetupBranch ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="shrink-0 self-start"
                              disabled={switching}
                              onClick={() => void setupBranch(group)}
                            >
                              {t('select.setupBranch')}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </CardHeader>
                  ) : (
                    <CardHeader className="flex flex-col gap-0 p-0 sm:flex-row sm:items-stretch">
                      <button
                        type="button"
                        className={cn(
                          'group relative flex min-w-0 w-full flex-1 items-start gap-4 p-5 text-left transition-colors',
                          'hover:bg-surface-sunken/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset',
                          switching && 'pointer-events-none opacity-70',
                        )}
                        disabled={switching}
                        aria-busy={switching}
                        aria-label={t('select.openAria', { name: group.name })}
                        onClick={() => void openCampus(campusRow.church.id)}
                      >
                        <ChurchMark logoUrl={group.logoUrl} name={group.name} />
                        <div className="min-w-0 flex-1">
                          <h2 className="truncate text-card-title font-semibold text-fg">
                            {group.name}
                          </h2>
                          <p className="mt-0.5 truncate text-body-sm text-fg-secondary">
                            {campusLabel(campusRow, tBranches('switcher.headOffice'))}
                            <span className="text-fg-tertiary"> · </span>
                            {t(`membership.role.${campusRow.membership.systemRole}`)}
                          </p>
                        </div>
                        <ChevronRight
                          className="size-5 shrink-0 self-center text-fg-tertiary transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-brand"
                          aria-hidden
                        />
                      </button>
                      {canSetupBranch ? (
                        <div className="flex w-full shrink-0 items-stretch border-t border-border p-5 sm:w-auto sm:border-t-0 sm:border-l">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="shrink-0"
                            disabled={switching}
                            onClick={() => void setupBranch(group)}
                          >
                            {t('select.setupBranch')}
                          </Button>
                        </div>
                      ) : null}
                    </CardHeader>
                  )}

                  {multiCampus ? (
                    <CardContent className="space-y-3 pb-5">
                      <CampusPicker
                        group={group}
                        switching={switching}
                        lastChurchId={lastChurchId}
                        headOfficeLabel={tBranches('switcher.headOffice')}
                        onOpen={(churchId) => void openCampus(churchId)}
                        searchPlaceholder={t('select.campusSearch')}
                        searchEmpty={t('select.campusSearchEmpty')}
                        campusLabel={t('select.campusLabel')}
                        lastOpenedLabel={t('select.lastOpened')}
                        roleLabel={(role) => t(`membership.role.${role}`)}
                        clearLabel={tCommon('search.clear')}
                        openAria={(name) => t('select.openAria', { name })}
                      />
                    </CardContent>
                  ) : null}
                </Card>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

const CampusPicker = ({
  group,
  switching,
  lastChurchId,
  headOfficeLabel,
  onOpen,
  searchPlaceholder,
  searchEmpty,
  campusLabel: sectionLabel,
  lastOpenedLabel,
  roleLabel,
  clearLabel,
  openAria,
}: {
  group: ChurchOrgGroup;
  switching: boolean;
  lastChurchId: string | null;
  headOfficeLabel: string;
  onOpen: (churchId: string) => void;
  searchPlaceholder: string;
  searchEmpty: string;
  campusLabel: string;
  lastOpenedLabel: string;
  roleLabel: (role: ChurchMembershipSummary['membership']['systemRole']) => string;
  clearLabel: string;
  openAria: (name: string) => string;
}) => {
  const [query, setQuery] = useState('');
  const showSearch = group.campuses.length >= CAMPUS_SEARCH_THRESHOLD;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return group.campuses;
    return group.campuses.filter((row) => {
      const label = campusLabel(row, headOfficeLabel).toLowerCase();
      return label.includes(needle) || row.church.campusName.toLowerCase().includes(needle);
    });
  }, [group.campuses, headOfficeLabel, query]);

  const list = (
    <div className="flex flex-col gap-2">
      {filtered.map((row) => {
        const wasLast = row.church.id === lastChurchId;
        const label = campusLabel(row, headOfficeLabel);

        return (
          <button
            key={row.church.id}
            type="button"
            className={cn(
              'group relative flex w-full cursor-pointer items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors',
              wasLast
                ? 'border-brand/30 bg-brand/5 hover:border-brand/50 hover:bg-brand/10'
                : 'border-border bg-surface hover:border-brand/40 hover:bg-surface-sunken/60',
              switching && 'pointer-events-none opacity-70',
            )}
            disabled={switching}
            aria-label={openAria(label)}
            onClick={() => onOpen(row.church.id)}
          >
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className="text-body font-medium text-fg">{label}</span>
                {wasLast ? (
                  <span className="rounded-md bg-surface-sunken px-1.5 py-0.5 text-caption text-fg-secondary">
                    {lastOpenedLabel}
                  </span>
                ) : null}
              </span>
              <span className="mt-0.5 block text-caption text-fg-secondary">
                {roleLabel(row.membership.systemRole)}
              </span>
            </span>
            <ChevronRight
              className="size-5 shrink-0 self-center text-fg-tertiary transition-[color,transform] group-hover:translate-x-0.5 group-hover:text-brand"
              aria-hidden
            />
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-caption font-medium text-fg-secondary">{sectionLabel}</p>
        {showSearch ? (
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder={searchPlaceholder}
            clearLabel={clearLabel}
            debounceMs={0}
            className="max-w-none sm:max-w-xs"
            aria-label={searchPlaceholder}
          />
        ) : null}
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3.5 py-6 text-center text-body-sm text-fg-secondary">
          {searchEmpty}
        </p>
      ) : showSearch ? (
        <ScrollArea className="h-72 pr-1">{list}</ScrollArea>
      ) : (
        list
      )}
    </div>
  );
};

const ChurchMark = ({ logoUrl, name }: { logoUrl: string | null; name: string }) =>
  logoUrl ? (
    <img
      src={logoUrl}
      alt=""
      className="size-12 shrink-0 rounded-lg border border-border object-cover"
    />
  ) : (
    <span
      className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-sunken text-fg-tertiary"
      aria-hidden
    >
      <Building2 className="size-5" />
      <span className="sr-only">{name}</span>
    </span>
  );

const campusLabel = (row: ChurchMembershipSummary, headOfficeLabel: string): string => {
  if (!row.church.parentChurchId) {
    return `${row.church.campusName} (${headOfficeLabel})`;
  }
  return row.church.campusName;
};
